import { CHANGE_BASELINE_MODEL, MAX_CHANGE_CRITERIA, MAX_CRITERION_MINUTES } from '@detect/shared';
import type { BaselineFinding, ChangeCriterion, ChangeGradingMode, ChangeIntentView, ChangeScopeRefInput, ChangeVerdictKind, ChangeVerdictView, SdtScopeType } from '@detect/shared';

/** Pure presentation helpers for the change-window UI (#26). */

export const VERDICT_LABELS: Record<ChangeVerdictKind, string> = {
  clean: 'Clean',
  'did-not-recover': 'Did not recover',
  'got-worse': 'Got worse',
  'blast-radius': 'Blast radius',
  indeterminate: 'Could not verify',
  abandoned: 'Abandoned',
};

export const VERDICT_EXPLANATIONS: Record<ChangeVerdictKind, string> = {
  clean: 'Everything in scope came back without a new or worse condition.',
  'did-not-recover': 'A new condition on an expected-impact entity was still there at the end of the window.',
  'got-worse': 'An existing condition on an expected-impact entity is more severe than before the window.',
  'blast-radius': 'An entity that was meant to stay unaffected (a witness) was impacted.',
  indeterminate: 'There was not enough fresh data to judge. This is never treated as clean.',
  abandoned: 'The window was deleted before it ended.',
};

const SCOPE_LABELS: Record<SdtScopeType, string> = {
  tenant: 'Whole tenant',
  client: 'Client',
  group: 'Group',
  resource: 'Resource',
  instance: 'Instance',
  sensor: 'Sensor',
  collector: 'Collector',
};

/**
 * "Resource: web-01". Never an internal id (tenant-facing rule): a resource, client or collector whose name is not known
 * (deleted, or not loaded) reads "Resource: (removed)"; an instance or sensor scope, which carries no name here, is just its
 * kind; a group scope's target IS its name. A tenant scope has no target.
 */
export function describeScope(
  ref: { scopeType: SdtScopeType; targetId?: string | null },
  names: ReadonlyMap<string, string> = new Map()
): string {
  const label = SCOPE_LABELS[ref.scopeType];
  if (ref.scopeType === 'tenant' || !ref.targetId) return label;
  if (ref.scopeType === 'group') return `${label}: ${ref.targetId}`;
  if (ref.scopeType === 'resource' || ref.scopeType === 'client' || ref.scopeType === 'collector') {
    return `${label}: ${names.get(ref.targetId) ?? '(removed)'}`;
  }
  return label;
}

export interface ScopeDraft {
  scopeType: 'tenant' | 'client' | 'resource' | 'collector';
  targetId: string;
}

/** Drafts → API refs. A tenant scope carries no target; every other scope needs one
 *  (an unfilled row is a validation error, never silently dropped). */
export function draftsToScopeRefs(drafts: readonly ScopeDraft[]): ChangeScopeRefInput[] {
  return drafts.map((d, i) => {
    if (d.scopeType === 'tenant') return { scopeType: 'tenant' };
    if (!d.targetId) throw new Error(`Choose a target for row ${i + 1}.`);
    return { scopeType: d.scopeType, targetId: d.targetId };
  });
}

/** True when a window is a one-off that has already ended (nothing left to run). */
export function isPastWindow(w: { endsAt: string | null }, now: number = Date.now()): boolean {
  return w.endsAt !== null && Date.parse(w.endsAt) < now;
}

/** The most occurrences the verdicts endpoint returns (newest first, no paging). */
export const VERDICT_LIMIT = 50;

/** The stand-in for a resource whose name the caller did not supply: the internal id is never shown. */
const NAMELESS = (): string => 'a resource';

const fmt = (n: number | null): string => (n === null ? '?' : String(Math.round(n * 100) / 100));

/** One outside-normal series, in words: "web-01 ping/rtt: 90 (above; normal 5 to 20)". Never shows the internal resource id. */
export function describeBaselineFinding(f: BaselineFinding, resourceName: (id: string) => string = NAMELESS): string {
  const series = `${resourceName(f.resourceId)} ${f.sensorKey}${f.instanceId ? `[${f.instanceId}]` : ''}/${f.datapoint}`;
  return `${series}: ${fmt(f.observed)} (${f.position} normal ${fmt(f.p05)} to ${fmt(f.p95)})`;
}

/**
 * Rung 2 (#153) in plain language, ADVISORY: it sits beside the verdict and never changes it. "No baseline yet" is
 * said as such and is never worded as normal; a comparison that is pending or could not be made says so.
 * Null when it does not apply (nothing was graded, or the change was abandoned).
 */
export function describeBaseline(
  v: Pick<ChangeVerdictView, 'baseline'>,
  resourceName: (id: string) => string = NAMELESS
): { summary: string; lines: string[] } | null {
  const b = v.baseline;
  switch (b.state) {
    case 'not-applicable':
      return null;
    case 'pending':
      return { summary: `Compared with normal: not yet. It is made ${CHANGE_BASELINE_MODEL.settleMinutes} minutes after the window ends.`, lines: [] };
    case 'unavailable':
      return { summary: 'Compared with normal: could not be made. This says nothing about whether the systems are normal.', lines: [] };
    case 'done': {
      const f = b.findings;
      if (!f) return null;
      const unreported = f.noData > 0 ? ` ${f.noData} more did not report in the period.` : '';
      if (f.status === 'no-baseline') {
        return { summary: `Compared with normal: no baseline yet for ${f.noBaseline} measurement${f.noBaseline === 1 ? '' : 's'} (too little history), so this was not checked. It is not a sign they are normal.${unreported}`, lines: [] };
      }
      if (f.status === 'no-data') {
        return { summary: `Compared with normal: nothing reported in the ${CHANGE_BASELINE_MODEL.settleMinutes} minutes after the window, so this was not checked.`, lines: [] };
      }
      const noBaseline = f.noBaseline > 0 ? ` ${f.noBaseline} more had no baseline yet.` : '';
      const noData = f.noData > 0 ? ` ${f.noData} more did not report in the period, so were not checked.` : '';
      const summary =
        f.outside === 0
          ? `Compared with normal: all ${f.assessed} measurements checked are within their usual range.${noBaseline}${noData}`
          : `Compared with normal: ${f.outside} of ${f.assessed} measurements checked are outside their usual range.${noBaseline}${noData}`;
      const lines = f.findings.slice(0, 5).map((x) => describeBaselineFinding(x, resourceName));
      if (f.outside > lines.length) lines.push(`and ${f.outside - lines.length} more`);
      return { summary, lines };
    }
  }
}

/* ---------------- rung 3 (#153): did it achieve its intent ---------------- */

export const GRADING_MODE_LABELS: Record<ChangeGradingMode, string> = {
  none: 'Not graded',
  structured: 'Graded from criteria',
  human: 'A person decides',
  ai: 'Analysed by AI',
};

/** A success-criterion row of the plan form, as typed (everything a string until it is validated). */
export interface CriterionDraft {
  kind: ChangeCriterion['kind'];
  resourceId: string;
  sensorKey: string;
  /** Empty = the resource-level series. */
  instanceId: string;
  datapoint: string;
  comparator: 'below' | 'above';
  threshold: string;
  minutes: string;
}

export const emptyCriterionDraft = (): CriterionDraft => ({
  kind: 'metric', resourceId: '', sensorKey: '', instanceId: '', datapoint: '', comparator: 'below', threshold: '', minutes: '30',
});

const SENSOR_KEY = /^[a-z][a-z0-9_.-]*$/;
const DATAPOINT = /^[A-Za-z][A-Za-z0-9_.]*$/;

/** Drafts → API criteria. An incomplete or malformed row is an error naming the row, never silently dropped. */
export function draftsToCriteria(drafts: readonly CriterionDraft[]): ChangeCriterion[] {
  if (drafts.length === 0) throw new Error('Add at least one success criterion.');
  if (drafts.length > MAX_CHANGE_CRITERIA) throw new Error(`At most ${MAX_CHANGE_CRITERIA} criteria.`);
  return drafts.map((d, i) => {
    const row = `Criterion ${i + 1}`;
    if (!d.resourceId) throw new Error(`${row}: choose a resource.`);
    const sensorKey = d.sensorKey.trim();
    if (!SENSOR_KEY.test(sensorKey)) throw new Error(`${row}: the sensor key is lowercase letters, digits and . _ - (for example ping).`);
    const instanceId = d.instanceId.trim() === '' ? null : d.instanceId.trim();
    if (instanceId === '-') throw new Error(`${row}: "-" cannot be an instance.`);
    const minutes = Number(d.minutes);
    const min = d.kind === 'sensor-ok' ? 0 : 1;
    if (d.minutes.trim() === '' || !Number.isInteger(minutes) || minutes < min || minutes > MAX_CRITERION_MINUTES) {
      throw new Error(`${row}: minutes must be a whole number from ${min} to ${MAX_CRITERION_MINUTES}.`);
    }
    const series = { resourceId: d.resourceId, sensorKey, instanceId };
    if (d.kind === 'sensor-ok') return { kind: 'sensor-ok', ...series, atMinutes: minutes };
    const datapoint = d.datapoint.trim();
    if (!DATAPOINT.test(datapoint)) throw new Error(`${row}: enter the datapoint name (for example rtt).`);
    if (d.kind === 'alert-cleared') return { kind: 'alert-cleared', ...series, datapoint, forMinutes: minutes };
    const threshold = Number(d.threshold);
    if (d.threshold.trim() === '' || !Number.isFinite(threshold)) throw new Error(`${row}: the threshold must be a number.`);
    return { kind: 'metric', ...series, datapoint, comparator: d.comparator, threshold, withinMinutes: minutes };
  });
}

/** A criterion in words, e.g. "ping/rtt on web-01 is below 100 within 30 min after the window". */
export function describeCriterion(c: ChangeCriterion, resourceName: (id: string) => string = NAMELESS): string {
  const where = `${resourceName(c.resourceId)}${c.instanceId ? ` [${c.instanceId}]` : ''}`;
  if (c.kind === 'metric') return `${c.sensorKey}/${c.datapoint} on ${where} is ${c.comparator} ${c.threshold} within ${c.withinMinutes} min after the window`;
  if (c.kind === 'alert-cleared') return `the ${c.sensorKey}/${c.datapoint} alert on ${where} is cleared and stays cleared for ${c.forMinutes} min after the window`;
  return `${c.sensorKey} on ${where} is ok ${c.atMinutes} min after the window`;
}

const INTENT_RESULT_LABELS: Record<ChangeIntentView['result'], string> = {
  achieved: 'Achieved',
  not_achieved: 'Not achieved',
  unclear: 'Unclear',
  not_graded: 'Not graded',
};
const CRITERION_MARKS = { met: 'Met', not_met: 'Not met', indeterminate: 'Could not decide' } as const;

/**
 * Rung 3 in plain language, ADVISORY: it sits beside the verdict and never changes it. A pending or unavailable
 * grading is never worded as achieved, and "unclear" says what it means. Null when there is nothing to say.
 */
export function describeIntent(intent: ChangeIntentView): { summary: string; lines: string[] } | null {
  const lines: string[] = [];
  if (intent.structured) {
    for (const c of intent.structured.criteria) lines.push(`${CRITERION_MARKS[c.result]}: ${c.detail}`);
  }
  if (intent.ai) {
    const a = intent.ai.analysis;
    if (a.outcome === 'ok') {
      const pct = a.confidence === null ? '' : `, ${Math.round(a.confidence * 100)}% confident`;
      lines.push(`AI analysis${a.model ? ` (${a.model})` : ''}: ${INTENT_RESULT_LABELS[intent.ai.result].toLowerCase()}${pct}. ${a.rationale}`);
    } else {
      lines.push(`AI analysis: ${a.rationale}`);
    }
  }
  for (const m of intent.marks.slice(0, 3)) {
    lines.push(`${m.result === 'achieved' ? 'Marked achieved' : 'Marked not achieved'} by ${m.markedBy} (${m.markedAt.slice(0, 16).replace('T', ' ')}Z)${m.note ? `: ${m.note}` : ''}`);
  }
  if (intent.result !== 'not_graded') {
    const by = intent.decidedBy === 'human' ? 'a person marked it' : intent.decidedBy === 'ai' ? 'AI analysis, advisory: check it' : 'from the success criteria';
    const unclear =
      intent.result !== 'unclear'
        ? ''
        : intent.decidedBy === 'ai'
          ? ' The AI could not give a clear answer, so it is not counted as achieved.'
          : ' Some criteria could not be decided (no data), so it is not counted as achieved.';
    const machine = intent.structured ?? intent.ai;
    const overridden = intent.decidedBy === 'human' && machine ? ` The ${intent.structured ? 'criteria' : 'AI'} said ${INTENT_RESULT_LABELS[machine.result].toLowerCase()}.` : '';
    return { summary: `Intent: ${INTENT_RESULT_LABELS[intent.result].toLowerCase()} (${by}).${unclear}${overridden}`, lines };
  }
  if (intent.state === 'pending') return { summary: 'Intent: not graded yet. It is graded once the last criterion is due.', lines };
  if (intent.state === 'unavailable') return { summary: 'Intent: could not be graded. This says nothing about whether it was achieved.', lines };
  if (intent.mode === 'human') return { summary: 'Intent: waiting for a person to mark it.', lines };
  return null;
}
